import React, {useCallback, useEffect, useRef, useState} from 'react';
import {inject, observer} from 'mobx-react';
import {StoryblocksMediaItem} from '../../../data/models/Storyblocks/StoryblocksMediaItem';
import StoryblocksStore from '../../../modules/BrowseStoryblocks/store/StoryblocksStore';
import LoadingSpinner from '../../../uiToolkit/LoadingSpinner/LoadingSpinner';
import SearchInput2 from '../../../uiToolkit/Inputs/SearchInput2/SearchInput2';
import {appText} from '../../../assets/text/appText';
import EditMediaItemCard from './EditMediaItemCard';
import {MediaPickerResult} from './editMediaTypes';
import styles from './EditMediaBrowserPanel.module.css';

const PAGE_SIZE = 50;

const itemToResult = (item: StoryblocksMediaItem): MediaPickerResult => {
  const previewUrl =
    item.preview_urls?._480p ||
    item.preview_urls?._720p ||
    item.preview_urls?._360p ||
    item.preview_urls?._180p ||
    '';
  return {
    source: 'storyblocks',
    libraryMediaFileId: null,
    storyBlocksVideoId: item.id,
    thumbnailUrl: item.thumbnail_url || '',
    previewUrl,
    fileDuration: item.duration || 0,
    kind: 'video',
  };
};

interface Props {
  organizationId: string;
  chunkId: string;
  pending: MediaPickerResult | null;
  onPick: (result: MediaPickerResult | null) => void;
  disabled?: boolean;
  storyblocksStore?: StoryblocksStore;
}

const EditMediaStoryblocksSource = inject('storyblocksStore')(
  observer(
    ({
      organizationId,
      chunkId,
      pending,
      onPick,
      disabled,
      storyblocksStore,
    }: Props) => {
      const [page, setPage] = useState(0);
      const [search, setSearch] = useState('');
      const [tmpSearch, setTmpSearch] = useState('');
      const [isLoading, setIsLoading] = useState(false);
      const isLoadingRef = useRef(false);
      const bottomRef = useRef<HTMLDivElement | null>(null);

      const loadData = useCallback(
        async (pageToLoad: number, query: string) => {
          if (isLoadingRef.current || !storyblocksStore) return;
          isLoadingRef.current = true;
          setIsLoading(true);
          await storyblocksStore.appendStoryblocksMedia(
            organizationId,
            chunkId,
            pageToLoad,
            PAGE_SIZE,
            query,
          );
          setPage(pageToLoad + 1);
          setIsLoading(false);
          isLoadingRef.current = false;
        },
        [organizationId, chunkId, storyblocksStore],
      );

      useEffect(() => {
        if (!storyblocksStore) return;
        storyblocksStore.reset();
        setPage(0);
        loadData(0, search);
      }, [search, storyblocksStore, loadData]);

      useEffect(() => {
        if (tmpSearch === '' && search !== '') setSearch('');
      }, [tmpSearch, search]);

      useEffect(() => {
        if (!storyblocksStore?.hasMore || isLoading) return;
        const observer = new IntersectionObserver(
          entries => {
            if (entries[0].isIntersecting) {
              loadData(page, search);
            }
          },
          {threshold: 1.0},
        );
        const el = bottomRef.current;
        if (el) observer.observe(el);
        return () => {
          if (el) observer.unobserve(el);
        };
      }, [isLoading, storyblocksStore?.hasMore, page, search, loadData]);

      const onSelectCard = (item: StoryblocksMediaItem) => {
        const result = itemToResult(item);
        const same =
          pending?.source === 'storyblocks' &&
          pending?.storyBlocksVideoId === item.id;
        onPick(same ? null : result);
      };

      if (!storyblocksStore) return null;

      const items = storyblocksStore.mediaItems;

      return (
        <>
          <div className={styles.searchRow}>
            <div className={styles.searchWrapper}>
              <SearchInput2
                value={tmpSearch}
                onSearch={setSearch}
                onTyping={setTmpSearch}
                disabled={disabled}
              />
            </div>
          </div>

          <div className={styles.content}>
            {isLoading && items.length === 0 && (
              <div className={`${styles.loading} ${styles.loadingFull}`}>
                <LoadingSpinner />
              </div>
            )}
            {!isLoading && items.length === 0 && !storyblocksStore.hasMore && (
              <p className={styles.empty}>{appText.noMoreItems}</p>
            )}
            <div className={styles.grid}>
              {items.map(item => {
                const previewUrl =
                  item.thumbnail_url ||
                  item.preview_urls?._360p ||
                  item.preview_urls?._480p ||
                  '';
                const isSelected =
                  pending?.source === 'storyblocks' &&
                  pending?.storyBlocksVideoId === item.id;
                return (
                  <EditMediaItemCard
                    key={item.id}
                    thumbnailUrl={previewUrl}
                    title={item.title}
                    duration={item.duration || 0}
                    selected={isSelected}
                    disabled={disabled}
                    onClick={() => onSelectCard(item)}
                  />
                );
              })}
              {items.length > 0 && storyblocksStore.hasMore && (
                <div ref={bottomRef} className={styles.loadMoreTrigger} />
              )}
            </div>
            {isLoading && items.length > 0 && (
              <div className={styles.loading}>
                <LoadingSpinner />
              </div>
            )}
          </div>
        </>
      );
    },
  ),
);

export default EditMediaStoryblocksSource;
